import { Box, Flex } from "@rebass/grid";
import { ISavedApplicationFilter } from "@rentiohq/shared-frontend/dist/reduxV2/applicationFilters/types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  DropdownMenu,
  ModalConfirmation,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { ActionList } from "@rentiohq/web-shared/dist/components/ActionList";
import React, { useState } from "react";
import { RM_COLORS } from "../../rentmatch.colors";

interface IProps {
  savedFilters: ISavedApplicationFilter[];
  activeFilterId?: number;
  onSelectFilter: (filter: ISavedApplicationFilter | undefined) => void;
  onShowAllFilters: () => void;
  onDeleteFilter: (id: number) => void;
  onTogglePanel: () => void;
  isPanelOpen: boolean;
  activeFilterCount: number;
}

const PencilIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const FiltersDropdown: React.FC<IProps> = ({
  savedFilters,
  activeFilterId,
  onSelectFilter,
  onDeleteFilter,
  onTogglePanel,
  isPanelOpen,
  activeFilterCount,
  onShowAllFilters,
}) => {
  const [filterToDeleteId, setFilterToDeleteId] = useState<number | null>(null);

  const activeFilterName = savedFilters.find(f => f.id === activeFilterId)
    ?.name;

  // The button shows its active (green/primary) style whenever a saved filter
  // is selected, the panel is open, or any ad-hoc filter criteria are applied.
  const isActive = !!activeFilterId || isPanelOpen || activeFilterCount > 0;

  const triggerButton = (
    <Button
      appearance={isActive ? "primary" : "outline"}
      color={isActive ? "primary" : "gray"}
      // Active: let the primary appearance paint it green. Inactive: give the
      // outline button a solid white background so it reads as a pill on the
      // toolbar (the appearance alone is transparent).
      style={isActive ? undefined : { backgroundColor: RM_COLORS.white }}
      iconAfterName={savedFilters.length > 0 ? "chevronDown" : undefined}
      onClick={savedFilters.length === 0 ? onTogglePanel : undefined}
    >
      <Flex alignItems="center">
        <Box
          style={{
            maxWidth: "150px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {activeFilterName || getLocalizedText("application.filters.title")}
        </Box>
        {activeFilterCount > 0 && (
          <Flex
            ml={2}
            alignItems="center"
            justifyContent="center"
            style={{
              backgroundColor: isActive ? "white" : RM_COLORS.rentmatchGreen,
              color: isActive ? RM_COLORS.rentmatchGreen : "white",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {activeFilterCount}
          </Flex>
        )}
        {(activeFilterId || activeFilterCount > 0) && (
          <Box
            ml={2}
            onClick={e => {
              e.stopPropagation();
              onSelectFilter(undefined);
              if (isPanelOpen) onTogglePanel();
            }}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <CloseIcon />
          </Box>
        )}
      </Flex>
    </Button>
  );

  if (savedFilters.length === 0) {
    return triggerButton;
  }

  const filterActions: any[] = [];

  savedFilters.forEach(filter => {
    filterActions.push({
      content: (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          style={{ width: "220px" }}
        >
          <Box
            flex="1"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginRight: "16px",
            }}
          >
            {filter.name} ({Object.keys(filter.criteria || {}).length})
          </Box>
          <Flex alignItems="center" style={{ flexShrink: 0, gap: "12px" }}>
            <div
              style={{
                color: RM_COLORS.rentmatchGreen,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
              title={getLocalizedText("application.filters.edit")}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onSelectFilter(filter);
                if (!isPanelOpen) onTogglePanel();
                document.body.click(); // Close the dropdown menu
              }}
            >
              <PencilIcon />
            </div>
            <div
              style={{
                color: RM_COLORS.redAlt,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
              title={getLocalizedText("application.filters.delete")}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setFilterToDeleteId(filter.id);
                document.body.click(); // Close the dropdown menu
              }}
            >
              <TrashIcon />
            </div>
          </Flex>
        </Flex>
      ),
      onClick: () => {
        onSelectFilter(filter);
        if (isPanelOpen) onTogglePanel();
      },
    });
  });

  const footerAction = {
    content: (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        style={{ width: "220px" }}
      >
        <Box color={RM_COLORS.rentmatchGreen} style={{ fontWeight: 600 }}>
          {getLocalizedText("application.filters.all")} ({activeFilterCount})
        </Box>
        <Box
          color={RM_COLORS.rentmatchGreen}
          style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
        >
          <PencilIcon />
        </Box>
      </Flex>
    ),
    onClick: () => {
      onShowAllFilters();
      if (!isPanelOpen) onTogglePanel();
    },
  };

  return (
    <>
      <DropdownMenu
        passDownClickEvents={true}
        shouldCloseOnClick={true}
        renderContent={close => (
          <>
            <ActionList
              heading={getLocalizedText("application.filters.saved_title")}
              items={filterActions}
              onClick={() => close()}
            />
            <ActionList items={[footerAction]} onClick={() => close()} />
          </>
        )}
      >
        {triggerButton}
      </DropdownMenu>

      {filterToDeleteId && (
        <ModalConfirmation
          heading={getLocalizedText("application.filters.delete.title")}
          actionsLabels={{
            cancel: getLocalizedText("application.filters.delete.cancel"),
            confirm: getLocalizedText(
              "application.filters.delete.confirm_button",
            ),
          }}
          onConfirm={() => {
            onDeleteFilter(filterToDeleteId);
            setFilterToDeleteId(null);
          }}
          onCancel={() => {
            setFilterToDeleteId(null);
          }}
        >
          <TextStyle>
            {getLocalizedText("application.filters.delete.confirm")}
          </TextStyle>
        </ModalConfirmation>
      )}
    </>
  );
};
